<template>
  <ontos-shell-menu :label="$tt('workspace')" :value="workspace.title"
                    :loading="loading" @toggle="onToggle">
    <!-- O ambiente atual entra na lista, e não é decoração: clicá-lo RECARREGA. Estando
         mais fundo, o :to já leva à home dele; estando nela, o vue-router recusa a
         navegação repetida e sem o @click não aconteceria nada — que é exatamente quando
         se quer recarregar. -->
    <v-list-item v-for="option of options" :key="option.alias"
                 :to="routeOf(option)" :title="option.title"
                 :active="option.alias === workspace.alias"
                 @click="onPick(option)">
      <!-- O selo também aqui: mostrados os encerrados, a lista precisa dizer qual é qual. -->
      <template #append v-if="option.closed">
        <v-chip size="x-small" variant="flat">{{ $tt('closed') }}</v-chip>
      </template>
    </v-list-item>
    <v-divider v-if="options.length"/>
    <!-- O ícone vem do `HOME_ICON`: este item e os destinos da barra levam ao MESMO lugar, e no
         telefone os dois menus ficam lado a lado — com ícones diferentes, leriam como dois
         lugares diferentes. -->
    <v-list-item :to="{name: 'home'}" :title="$tt('workspaces-all')"
                 :prepend-icon="HOME_ICON"/>
  </ontos-shell-menu>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {EnhancedWorkpace} from '@/types/Workspace'
import {CredentialError} from '@/errors/errors'
import ShellMenu from '@/components/shell/ShellMenu.vue'
import {HOME_ICON} from '@/utils/destinations'
import {WorkspaceOption, workspaceOptions} from '@/services/SystemService'

/**
 * O ambiente atual na barra, e o caminho para os outros.
 *
 * A lista é a do `SystemService` (`workspaceOptions`), que criar, alterar, encerrar e excluir um
 * ambiente já buscam de novo — o menu se atualiza na hora, sem ser aberto (pedido do usuário).
 *
 * Ela é buscada **assim que a barra aparece pela primeira vez**, em segundo plano, para o menu já
 * abrir pronto (pedido do usuário: só carregar no primeiro clique fazia esperar). Uma vez por
 * sessão, e não por tela: guardada no módulo do serviço, ela atravessa as remontagens desta barra,
 * e só é buscada de novo quando não há nenhuma. Antes ela esperava a primeira abertura, para não
 * custar uma requisição por navegação — o que o guardado já evita.
 *
 * Nas aberturas do menu ela aparece na hora, do que está guardado, e é buscada de novo em
 * segundo plano — sem barra de progresso, porque já há o que mostrar. É o que a mantém correta
 * quando OUTRA pessoa cria um ambiente, que este navegador não tinha como saber.
 *
 * O ambiente atual aparece na lista e continua clicável, porque clicá-lo é o jeito de
 * recarregar o que o servidor tem — útil quando alguém mexeu no design em outro lugar.
 * A lista guardada NÃO é atualizada nesse caso: o que se recarrega é o ambiente aberto,
 * e a lista de ambientes muda muito menos.
 *
 * Este não é um RouteComponent, então não herda o tratamento de erro da base — daí o
 * catch próprio, que devolve a credencial vencida ao login e o resto para a barra de
 * mensagens do App.
 */
@Component({
  name: 'ontos-workspace-menu',
  emits: ['showErrorMessage', 'reload'],
  components: {'ontos-shell-menu': ShellMenu}
})
class WorkspaceMenu extends Vue {
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace

  loading = false
  //Exposta ao template: constante de módulo não é enxergada de lá.
  HOME_ICON = HOME_ICON

  /**
   * A lista para o menu. É getter, e não o campo direto, porque `systems` é null até a
   * primeira carga terminar — e o menu já está desenhado nesse instante.
   *
   * **Encerrado não aparece — menos aquele em que se está.** Esconder o atual faria o menu
   * dizer que você está em lugar nenhum: ele mostra o ambiente corrente como ativo, e a linha
   * ativa precisa existir. Quem quer ver todos os encerrados tem a lista da home, que é onde
   * mora o interruptor; aqui é um menu de navegação, e menu não ganha interruptor.
   */
  /** A lista guardada no serviço, ou null antes da primeira busca. */
  get systems(): WorkspaceOption[] | null {
    return workspaceOptions.value
  }

  get options(): WorkspaceOption[] {
    return (this.systems || [])
        .filter(system => !system.closed || system.alias === this.workspace.alias)
  }

  onPick(system: { alias: string }) {
    if (system.alias === this.workspace.alias) this.$emit('reload')
  }

  /**
   * Trocar de ambiente leva ao início do outro. Ia ao "mesmo modo" de lá — o design do outro, de
   * dentro do design —, e os modos saíram (ver `utils/destinations`).
   */
  routeOf(system: WorkspaceOption) {
    return {name: 'view-system', params: {system: system.alias}}
  }

  /** A busca antecipada, só quando ainda não há lista guardada — ver o comentário da classe. */
  mounted() {
    if (!this.systems) this.load(false)
  }

  async onToggle(open: boolean) {
    if (!open || this.loading) return
    await this.load(true)
  }

  /**
   * Busca a lista. `opened`: foi o usuário que abriu o menu — só então há quem espere por ela e a
   * quem dizer que falhou. A busca antecipada falha em silêncio: ninguém pediu nada, e quem abrir o
   * menu tenta de novo, com a mensagem.
   */
  async load(opened: boolean) {
    //Só espera quem não tem o que mostrar
    this.loading = opened && !this.systems
    try {
      await this.$systems.refreshOptions()
    } catch (err) {
      if (!opened) return
      if (err instanceof CredentialError) await this.$router.push({name: 'login'})
      //Com lista guardada, a falha da atualização em segundo plano é silenciosa: o menu
      //já mostra algo utilizável, e um erro aqui seria ruído sobre uma ação que o
      //usuário nem pediu.
      else if (!this.systems) this.$emit('showErrorMessage', (err as Error).message)
    } finally {
      this.loading = false
    }
  }
}

export default toNative(WorkspaceMenu)
</script>
